import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Lock, ChevronLeft, ChevronRight, CheckCircle2, ShieldCheck, Info, Plus, MessageSquare, Send, CalendarCheck } from "lucide-react";
import api from "../lib/api";

const PURPLE = "#7737F6";

function Guidance({ html }) {
  return (
    <div className="flex gap-2 text-[13px] leading-relaxed text-zinc-600 bg-[#F7F4FE] border-l-2 border-[#7737F6] px-3 py-2 rounded-r">
      <Info size={15} className="mt-0.5 shrink-0 text-[#7737F6]" />
      <span dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

export default function ClientSelfAssessment() {
  const { ref } = useParams();
  const [state, setState] = useState({ loading: true, error: null, meta: null, bundle: null });
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [codeErr, setCodeErr] = useState("");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});   // { runId: { indicatorId: {score} } }
  const [e360, setE360] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [done, setDone] = useState(false);
  const [picked, setPicked] = useState([]);
  const [selecting, setSelecting] = useState(false);
  const [forceSelect, setForceSelect] = useState(false);
  const [adding, setAdding] = useState("");
  const [results, setResults] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);

  const loadResults = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/self-assessment/${ref}/results`);
      setResults(data.summary);
      setChatMsgs(data.transcript || []);
      setBooked(!!data.booking_requested);
      setDone(true);
    } catch { setDone(true); }
  }, [ref]);

  const sendChat = async (text) => {
    const msg = (text || chatInput).trim();
    if (!msg || chatSending) return;
    setChatSending(true); setChatInput("");
    setChatMsgs((m) => [...m, { role: "client", text: msg }]);
    try {
      const { data } = await api.post(`/public/self-assessment/${ref}/chat`, { message: msg });
      setChatMsgs(data.transcript || []);
    } catch { setChatMsgs((m) => [...m, { role: "advisor", text: "Sorry — I couldn't respond just now. Please try again, or request a session below." }]); }
    setChatSending(false);
  };

  const requestBooking = async () => {
    setBooking(true);
    try { await api.post(`/public/self-assessment/${ref}/book`); setBooked(true); }
    catch { /* keep button */ }
    setBooking(false);
  };

  const hydrate = (bundle) => {
    const a = {};
    Object.entries(bundle.progress || {}).forEach(([rid, p]) => { a[rid] = { ...(p.answers || {}) }; });
    setAnswers(a);
    setE360(bundle.e360_profile || {});
  };

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/self-assessment/${ref}`);
      if (data.completed && data.verified) { await loadResults(); setState((s) => ({ ...s, loading: false, meta: data })); return; }
      if (data.verified && data.bundle) hydrate(data.bundle);
      setState({ loading: false, error: null, meta: data, bundle: data.verified ? data.bundle : null });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || "This link is unavailable.", meta: null, bundle: null });
    }
  }, [ref, loadResults]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const verify = async () => {
    setVerifying(true); setCodeErr("");
    try {
      const { data } = await api.post(`/public/self-assessment/${ref}/verify`, { code });
      if (data.completed) { setState((s) => ({ ...s, meta: { ...s.meta, verified: true } })); await loadResults(); }
      else { hydrate(data.bundle); setState((s) => ({ ...s, bundle: data.bundle, meta: { ...s.meta, verified: true } })); }
    } catch (e) {
      setCodeErr(e?.response?.data?.detail || "That code didn't work.");
    }
    setVerifying(false);
  };

  const bundle = state.bundle;

  // Build the step list: one step per framework dimension, then one E360 step.
  const steps = useMemo(() => {
    if (!bundle) return [];
    const out = [];
    (bundle.frameworks || []).forEach((fw) => {
      const runId = bundle.framework_run_map[fw.id];
      fw.dimensions.forEach((dim) => out.push({ type: "dim", fw, runId, dim }));
    });
    if (bundle.include_e360 && bundle.e360) out.push({ type: "e360", dims: bundle.e360.dimensions });
    return out;
  }, [bundle]);

  const totals = useMemo(() => {
    if (!bundle) return { total: 0, answered: 0 };
    let total = 0, answered = 0;
    (bundle.frameworks || []).forEach((fw) => {
      const runId = bundle.framework_run_map[fw.id];
      fw.dimensions.forEach((dim) => dim.indicators.forEach((ind) => {
        total += 1;
        if (answers[runId]?.[ind.id]?.score) answered += 1;
      }));
    });
    return { total, answered };
  }, [bundle, answers]);

  const e360Required = useMemo(() => {
    if (!bundle?.e360) return { req: 0, filled: 0 };
    const reqDims = bundle.e360.dimensions.filter((d) => d.required);
    return { req: reqDims.length, filled: reqDims.filter((d) => e360[d.id]).length };
  }, [bundle, e360]);

  const pct = useMemo(() => {
    const denom = totals.total + e360Required.req;
    if (!denom) return 0;
    return Math.round((100 * (totals.answered + e360Required.filled)) / denom);
  }, [totals, e360Required]);

  const setAnswer = async (runId, indId, score) => {
    setAnswers((a) => ({ ...a, [runId]: { ...(a[runId] || {}), [indId]: { score } } }));
    try { await api.patch(`/public/self-assessment/${ref}/answers`, { run_id: runId, answers: { [indId]: { score } } }); }
    catch { /* autosave best-effort; will re-save on navigation */ }
  };

  const setE360Field = async (id, value) => {
    setE360((p) => ({ ...p, [id]: value }));
    try { await api.patch(`/public/self-assessment/${ref}/answers`, { e360_profile: { [id]: value } }); }
    catch { /* best-effort */ }
  };

  const submit = async () => {
    setSubmitting(true); setSubmitErr("");
    try { await api.post(`/public/self-assessment/${ref}/submit`); await loadResults(); }
    catch (e) { setSubmitErr(e?.response?.data?.detail || "Not everything is complete yet."); }
    setSubmitting(false);
  };

  const confirmSelection = async () => {
    if (picked.length === 0) return;
    setSelecting(true);
    try {
      const { data } = await api.post(`/public/self-assessment/${ref}/select`, { framework_ids: picked });
      hydrate(data.bundle);
      setState((s) => ({ ...s, bundle: data.bundle }));
      setForceSelect(false);
      setStep(0);
    } catch (e) { setSubmitErr(e?.response?.data?.detail || "Could not save your selection."); }
    setSelecting(false);
  };

  const addAssessment = async (id) => {
    setAdding(id); setSubmitErr("");
    try {
      const next = [...(bundle.selected_framework_ids || []), id];
      const { data } = await api.post(`/public/self-assessment/${ref}/select`, { framework_ids: next });
      hydrate(data.bundle);
      setState((s) => ({ ...s, bundle: data.bundle }));
    } catch (e) { setSubmitErr(e?.response?.data?.detail || "Could not add that assessment."); }
    setAdding("");
  };

  // ---------------- render states ----------------
  const Shell = ({ children }) => (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <div className="bg-[#0B0B0C] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] tracking-[0.26em] text-[#A179FA] font-bold">EKSELENS CONSULTING</div>
            <div className="text-[13px] text-zinc-300 mt-1">Guided self-assessment</div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-zinc-400">
            <ShieldCheck size={13} className="text-[#A179FA]" /> Private &amp; secure link
          </div>
        </div>
      </div>
      <div className="flex-1 w-full max-w-3xl mx-auto px-6 py-8">{children}</div>
      <div className="border-t border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 text-[11.5px] text-zinc-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} Ekselens Consulting. Your responses are confidential.</span>
          <span>Progress saves automatically.</span>
        </div>
      </div>
    </div>
  );

  if (state.loading) return <Shell><div className="text-center py-20 text-zinc-400"><Loader2 className="animate-spin inline" size={28} /></div></Shell>;
  if (state.error) return <Shell><div data-testid="sa-error" className="text-center py-16 text-zinc-600">{state.error}</div></Shell>;
  if (done) return (
    <Shell>
      <div data-testid="sa-thankyou" className="space-y-5">
        <div className="text-center pt-4">
          <CheckCircle2 size={44} className="text-emerald-500 mx-auto" />
          <h1 className="text-2xl font-semibold text-zinc-900 mt-3">Thank you — that's everything we need.</h1>
          <p className="text-zinc-600 mt-2 max-w-lg mx-auto">Here's a first look at your results. Ask our AI advisor anything about them below — then our team validates everything and prepares your board-grade findings.</p>
        </div>

        {results && (
          <div data-testid="sa-results-summary" className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#7737F6] font-semibold">Your results at a glance</div>
            <div className="mt-3 space-y-3">
              {(results.frameworks || []).map((fw, i) => (
                <div key={i} className="border-b border-zinc-100 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-baseline justify-between">
                    <span className="font-medium text-zinc-900">{fw.sub_brand}</span>
                    <span className="text-[13px] text-zinc-500">Maturity: <b className="text-zinc-800">{fw.overall_level || "n/a"}</b>{fw.overall_weighted ? ` · ${fw.overall_weighted}/5` : ""}</span>
                  </div>
                  {fw.weakest?.length > 0 && (
                    <div className="text-[12.5px] text-zinc-500 mt-1">Priority areas: {fw.weakest.map((w) => w.name).join(", ")}</div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-zinc-400 mt-3 italic">These self-assessed results are indicative until validated by Ekselens.</p>
          </div>
        )}

        {/* AI advisor chat */}
        <div data-testid="sa-chat" className="bg-white border border-zinc-200 rounded-xl p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#7737F6] font-semibold">
            <MessageSquare size={14} /> Ask your Ekselens AI advisor
          </div>
          <div className="mt-3 space-y-3 max-h-[320px] overflow-y-auto" data-testid="sa-chat-log">
            {chatMsgs.length === 0 && (
              <div className="flex flex-wrap gap-2">
                {(results?.frameworks ? ["What do these results mean for us?", "What should we prioritise first?", "What would it take to reach the next level?"] : []).map((q, i) => (
                  <button key={i} data-testid={`sa-suggested-${i}`} onClick={() => sendChat(q)}
                    className="text-[12.5px] border border-zinc-200 rounded-full px-3 py-1.5 hover:border-[#7737F6] hover:text-[#7737F6]">{q}</button>
                ))}
              </div>
            )}
            {chatMsgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "client" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] text-[13.5px] leading-relaxed rounded-2xl px-3.5 py-2 ${m.role === "client" ? "bg-[#7737F6] text-white" : "bg-zinc-100 text-zinc-800"}`}>{m.text}</div>
              </div>
            ))}
            {chatSending && <div className="text-[12px] text-zinc-400"><Loader2 size={13} className="animate-spin inline" /> advisor is typing…</div>}
          </div>
          <div className="mt-3 flex gap-2">
            <input data-testid="sa-chat-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Ask about your results…"
              className="flex-1 border border-zinc-300 rounded-full px-4 py-2 text-sm" />
            <button data-testid="sa-chat-send" onClick={() => sendChat()} disabled={chatSending || !chatInput.trim()}
              className="bg-zinc-900 text-white rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-40"><Send size={16} /></button>
          </div>
        </div>

        {/* Booking CTA */}
        <div className="bg-[#0B0B0C] text-white rounded-xl p-6 text-center">
          {booked ? (
            <div data-testid="sa-booked" className="flex items-center justify-center gap-2 text-emerald-300"><CalendarCheck size={18} /> Your session request is in — we'll be in touch shortly.</div>
          ) : (
            <>
              <h3 className="text-lg font-medium">Ready to turn this into a plan?</h3>
              <p className="text-[13.5px] text-zinc-400 mt-1 max-w-md mx-auto">Book a short strategy session — an Ekselens consultant will validate your results and quantify the value at stake.</p>
              <button data-testid="sa-book-btn" onClick={requestBooking} disabled={booking}
                className="mt-4 inline-flex items-center gap-2 bg-[#7737F6] text-white px-6 py-2.5 rounded text-[12px] tracking-[0.12em] uppercase disabled:opacity-40">
                {booking ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />} Request your strategy session
              </button>
            </>
          )}
        </div>
      </div>
    </Shell>
  );

  // Code gate
  if (!state.meta?.verified || !bundle) return (
    <Shell>
      <div data-testid="sa-code-gate" className="max-w-md mx-auto bg-white border border-zinc-200 rounded-xl p-6 mt-6">
        <Lock size={22} className="text-[#7737F6]" />
        <h1 className="text-xl font-semibold text-zinc-900 mt-3">Welcome{state.meta?.client_name ? `, ${state.meta.client_name}` : ""}</h1>
        <p className="text-sm text-zinc-600 mt-2">Please enter the one-time access code we sent you in a separate email to begin your assessment.</p>
        <input data-testid="sa-code-input" value={code} onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && code && verify()}
          placeholder="XXXX-XXXX" autoFocus
          className="mt-4 w-full border border-zinc-300 rounded px-3 py-2.5 text-center tracking-[0.2em] uppercase font-medium" />
        {codeErr && <div data-testid="sa-code-error" className="text-[13px] text-rose-600 mt-2">{codeErr}</div>}
        <button data-testid="sa-verify-btn" onClick={verify} disabled={verifying || !code}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#7737F6] text-white py-2.5 rounded text-[12px] tracking-[0.12em] uppercase disabled:opacity-40">
          {verifying ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Verify &amp; start
        </button>
      </div>
    </Shell>
  );

  // Selection screen (client_choice mode, nothing chosen yet — or re-opened mid-flow)
  if (bundle.needs_selection || forceSelect) return (
    <Shell>
      <div data-testid="sa-selection" className="bg-white border border-zinc-200 rounded-xl p-6">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#7737F6] font-semibold">Choose your assessments</div>
        <h1 className="text-xl font-semibold text-zinc-900 mt-1">Which areas would you like to assess?</h1>
        <p className="text-[13px] text-zinc-500 mt-1">Select one or more from the assessments we've prepared for you. The Enterprise360 profile is always included.{forceSelect ? " Answers you've already given for kept assessments are preserved." : ""}</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-2">
          {(bundle.offered || []).map((o) => {
            const on = picked.includes(o.id);
            return (
              <button key={o.id} type="button" data-testid={`sa-pick-${o.sub_brand}`}
                onClick={() => setPicked((p) => on ? p.filter((x) => x !== o.id) : [...p, o.id])}
                className={`text-left border rounded-lg px-3 py-3 transition-colors ${on ? "border-[#7737F6] bg-[#7737F6]/5" : "border-zinc-200 hover:border-zinc-300"}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-[#7737F6] border-[#7737F6]" : "border-zinc-300"}`}>
                    {on && <CheckCircle2 size={12} className="text-white" />}
                  </span>
                  <span className="font-medium text-[14px] text-zinc-900">{o.sub_brand}</span>
                </div>
                <div className="text-[12px] text-zinc-500 mt-1 ml-6">{o.name}</div>
              </button>
            );
          })}
        </div>
        {submitErr && <div className="text-[13px] text-rose-600 mt-3">{submitErr}</div>}
        <div className="mt-5 flex items-center gap-3">
          <button data-testid="sa-confirm-selection" onClick={confirmSelection} disabled={selecting || picked.length === 0}
            className="inline-flex items-center gap-2 bg-[#7737F6] text-white px-6 py-2.5 rounded text-[12px] tracking-[0.12em] uppercase disabled:opacity-40">
            {selecting ? <Loader2 size={15} className="animate-spin" /> : <ChevronRight size={15} />} {forceSelect ? "Save selection" : "Continue"}
          </button>
          {forceSelect && (
            <button data-testid="sa-cancel-selection" onClick={() => { setForceSelect(false); setSubmitErr(""); }}
              className="text-[13px] text-zinc-500 hover:text-zinc-800">Cancel</button>
          )}
        </div>
      </div>
    </Shell>
  );

  const cur = steps[step];
  const isLast = step === steps.length - 1;
  const remaining = bundle.mode === "client_choice"
    ? (bundle.offered || []).filter((o) => !(bundle.selected_framework_ids || []).includes(o.id))
    : [];

  return (
    <Shell>
      {/* progress */}
      <div className="sticky top-0 z-10 bg-zinc-50 pb-3">
        <div className="flex items-center justify-between text-[12px] text-zinc-500 mb-1">
          <span className="flex items-center gap-3">
            Step {step + 1} of {steps.length}
            {bundle.mode === "client_choice" && (
              <button data-testid="sa-change-assessments"
                onClick={() => { setPicked(bundle.selected_framework_ids || []); setForceSelect(true); }}
                className="text-[11px] text-[#7737F6] hover:underline">Change assessments</button>
            )}
          </span>
          <span data-testid="sa-progress-pct">{pct}% complete</span>
        </div>
        <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
          <div className="h-full bg-[#7737F6] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div data-testid="sa-wizard" className="bg-white border border-zinc-200 rounded-xl p-6 mt-2">
        {cur?.type === "dim" ? (
          <>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#7737F6] font-semibold">{cur.fw.sub_brand}</div>
            <h2 className="text-xl font-semibold text-zinc-900 mt-1">{cur.dim.name}</h2>
            <div className="mt-3"><Guidance html={cur.dim.guidance} /></div>
            <div className="mt-5 space-y-6">
              {cur.dim.indicators.map((ind) => (
                <div key={ind.id} data-testid={`sa-indicator-${ind.id}`}>
                  <div className="text-[15px] font-medium text-zinc-900">{ind.prompt}</div>
                  <div className="text-[12.5px] text-zinc-500 mt-1">{ind.guidance}</div>
                  <div className="mt-2 grid gap-2">
                    {ind.options.map((o) => {
                      const sel = answers[cur.runId]?.[ind.id]?.score === o.score;
                      return (
                        <button key={o.score} type="button"
                          data-testid={`sa-opt-${ind.id}-${o.score}`}
                          onClick={() => setAnswer(cur.runId, ind.id, o.score)}
                          className={`text-left border rounded-lg px-3 py-2.5 text-[13.5px] transition-colors flex gap-3 items-start ${sel ? "border-[#7737F6] bg-[#7737F6]/5" : "border-zinc-200 hover:border-zinc-300"}`}>
                          <span className={`mt-0.5 w-5 h-5 rounded-full border shrink-0 flex items-center justify-center text-[11px] font-bold ${sel ? "bg-[#7737F6] text-white border-[#7737F6]" : "border-zinc-300 text-zinc-400"}`}>{o.score}</span>
                          <span className={sel ? "text-zinc-900" : "text-zinc-700"}>{o.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : cur?.type === "e360" ? (
          <>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#7737F6] font-semibold">Enterprise360</div>
            <h2 className="text-xl font-semibold text-zinc-900 mt-1">About your organisation</h2>
            <p className="text-[13px] text-zinc-500 mt-1">These details calibrate your Reinvention Blueprint. Fields marked * are required.</p>
            <div className="mt-5 space-y-5">
              {cur.dims.map((d) => (
                <div key={d.id} data-testid={`sa-e360-${d.id}`}>
                  <label className="text-[14px] font-medium text-zinc-900">{d.label}{d.required ? " *" : ""}</label>
                  <div className="text-[12.5px] text-zinc-500 mt-0.5">{d.guidance}</div>
                  {d.free_text ? (
                    <textarea rows={2} value={e360[d.id] || ""} onChange={(e) => setE360Field(d.id, e.target.value)}
                      className="mt-2 w-full border border-zinc-300 rounded px-3 py-2 text-sm" />
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(d.options || []).map((opt) => {
                        const sel = e360[d.id] === opt;
                        return (
                          <button key={opt} type="button" data-testid={`sa-e360-${d.id}-${opt}`}
                            onClick={() => setE360Field(d.id, opt)}
                            className={`border rounded-full px-3 py-1.5 text-[12.5px] capitalize transition-colors ${sel ? "border-[#7737F6] bg-[#7737F6] text-white" : "border-zinc-200 text-zinc-700 hover:border-zinc-300"}`}>
                            {String(opt).replace(/_/g, " ")}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      {isLast && remaining.length > 0 && (
        <div data-testid="sa-add-another" className="mt-4 bg-white border border-zinc-200 rounded-xl p-4">
          <div className="text-[13px] font-medium text-zinc-900">Want to add another assessment before you submit?</div>
          <div className="text-[12px] text-zinc-500 mt-0.5">You can include any of these — your current answers are kept.</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {remaining.map((o) => (
              <button key={o.id} type="button" data-testid={`sa-add-${o.sub_brand}`}
                onClick={() => addAssessment(o.id)} disabled={adding === o.id}
                className="inline-flex items-center gap-1.5 border border-[#7737F6] text-[#7737F6] rounded-full px-3 py-1.5 text-[12.5px] hover:bg-[#7737F6]/5 disabled:opacity-40">
                {adding === o.id ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {o.sub_brand}
              </button>
            ))}
          </div>
        </div>
      )}

      {submitErr && <div data-testid="sa-submit-error" className="text-[13px] text-rose-600 mt-3">{submitErr}</div>}

      {/* nav */}
      <div className="flex items-center justify-between mt-5">
        <button data-testid="sa-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
          className="inline-flex items-center gap-1 text-[13px] text-zinc-600 disabled:opacity-30">
          <ChevronLeft size={16} /> Back
        </button>
        {isLast ? (
          <button data-testid="sa-submit" onClick={submit} disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#7737F6] text-white px-6 py-2.5 rounded text-[12px] tracking-[0.12em] uppercase disabled:opacity-40">
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Submit assessment
          </button>
        ) : (
          <button data-testid="sa-next" onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
            className="inline-flex items-center gap-1 bg-zinc-900 text-white px-5 py-2.5 rounded text-[12px] tracking-[0.12em] uppercase">
            Next <ChevronRight size={16} />
          </button>
        )}
      </div>
    </Shell>
  );
}
